/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Caption2, Caption3, Caption4, T3, Wrap } from 'components/Common';
import { Radiobox } from 'components/Common/Radio';
import Spacing from 'components/Common/Spacing';
import { Checkbox } from 'components/ui/checkbox';
import { auth } from 'features/signup';
import { AdminManager } from 'features/signup';
import useIsMobile from 'hooks/useIsMobile';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import useMutation from 'react-query/useMutation';
import { useSearchParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { userSelectorFamily } from 'recoil/login';
import { execDaumPostcode } from 'shared/lib';

import { useRegisterNextPage } from '..';
import RegisterWizard from '../mobile/RegisterWizard';
import { RegStep1, RegStep2, RegStep3 } from '../mobile/Steps';
import { registerSchema } from './schema';
import { Container, Input, Modal, ModalContainer, SearchButton, SubmitButton } from './styles';

const Languages = {
  ENGLISH: '영어',
  CHINESE: '중국어',
  JAPANESE: '일본어',
  VIETNAMESE: '베트남어',
};

const MainListings = {
  ROOM: '원룸/투룸',
  OFFICETEL: '오피스텔',
  APARTMENT: '아파트',
  SHAREHOUSE: '셰어하우스',
  OTHERS: '기타',
};

function RegisterInfoPage() {
  const [searchParams] = useSearchParams();
  const urlParams = new URLSearchParams(window.location.search);
  const isMobile = useIsMobile();

  const email = searchParams.get('email') || urlParams.get('email');
  const loginType = searchParams.get('loginType') || urlParams.get('loginType');
  // 가입 코드 단계가 제거되어 역할 분기가 없어졌다. 서버가 가입 시 ROLE_ADMIN으로 강제 처리한다.
  const memberRole = 'ROLE_ADMIN' as const;

  const [jwt] = useRecoilState(userSelectorFamily('jwt'));
  const methods = useForm({
    resolver: yupResolver(registerSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      email: email || '',
      agencyType: 'ESTATE',
      agencyName: '',
      agencyNameEN: '',
      registrationNumber: '',
      address: '',
      addressDetail: '',
      agencyPhoneNumber: '',
      firstName: '',
      lastName: '',
      firstNameEN: '',
      lastNameEN: '',
      phoneNumber: '',
      languages: [],
      mainListings: [],
      adminManagerInfos: [],
    },
  });
  const nextPage = useRegisterNextPage();
  const socialSignUpHook = useMutation(auth.socialSignUp);
  const emailSignUpHook = useMutation(auth.emailSignUp);

  const handleCheckArrayField = (field: any, value: string) => {
    methods.setValue(
      field,
      (methods.watch(field) || []).includes(value)
        ? (methods.watch(field) || []).filter((v: string) => v !== value)
        : [...(methods.watch(field) || []), value],
    );
  };

  const onSubmit = (data: typeof registerSchema.__outputType) => {
    if (loginType === 'kakao') {
      socialSignUpHook.mutate(
        {
          accessToken: jwt,
          ...data,
          addressEN: data.addressEN || '',
          agencyType: data.agencyType as 'ESTATE' | 'ACCOMMODATION',
          registrationNumber: data.registrationNumber || null,
          addressDetail: data.addressDetail || '',
          languages: data.languages || [],
          mainListings: data.mainListings || [],
          adminManagerInfos:
            data.adminManagerInfos?.map((adminManager) => ({
              ...adminManager,
              adminManagerId: null,
            })) || [],
          memberRole,
        },
        {
          onSuccess: () => {
            nextPage();
          },
          onError: (e) => {
            alert(e);
          },
        },
      );
    }

    if (loginType === 'email') {
      emailSignUpHook.mutate(
        {
          accessToken: jwt,
          ...data,
          addressEN: data.addressEN || '',
          agencyType: data.agencyType as 'ESTATE' | 'ACCOMMODATION',
          registrationNumber: data.registrationNumber || null,
          addressDetail: data.addressDetail || '',
          languages: data.languages || [],
          mainListings: data.mainListings || [],
          adminManagerInfos:
            data.adminManagerInfos?.map((adminManager) => ({
              ...adminManager,
              adminManagerId: null,
            })) || [],
          memberRole,
        },
        {
          onSuccess: () => {
            nextPage();
          },

          onError: (error) => {
            if (typeof error === 'string') {
              alert(error);
            }
          },
        },
      );
    }
  };

  function handleClickAddressSearch() {
    execDaumPostcode((address, addressEN) => {
      // 우편번호와 주소 정보를 해당 필드에 넣는다.
      methods.setValue('address', address);
      methods.setValue('addressEN', addressEN);
      // 커서를 상세주소 필드로 이동한다.
      methods.setFocus('address');
    });
  }

  // 제출 검증 실패 시 처리 (데스크탑/모바일 공용)
  const handleFormError = (errors: typeof methods.formState.errors) => {
    if (loginType === 'kakao' && Object.keys(errors).every((key) => key === 'password' || key === 'passwordConfirm')) {
      return onSubmit(methods.getValues());
    }

    const firstError = (Object.keys(errors) as Array<keyof typeof errors>).reduce<keyof typeof errors | null>(
      (field, a) => {
        const fieldKey = field as keyof typeof errors;
        if (loginType === 'kakao' && (fieldKey === 'password' || fieldKey === 'passwordConfirm')) {
          return a;
        }
        return !!errors[fieldKey] ? fieldKey : a;
      },
      null,
    );

    if (firstError) {
      const errorElement = document.getElementById(firstError as string);
      if (errorElement) {
        errorElement.scrollIntoView({ block: 'center' });
      } else methods.setFocus(firstError as any);
    }
  };

  if (isMobile) {
    const agencyType = methods.watch('agencyType');
    const step1Fields = [
      'agencyName',
      'agencyNameEN',
      'address',
      'addressDetail',
      'agencyPhoneNumber',
      ...(agencyType === 'ESTATE' ? ['registrationNumber'] : []),
      ...(loginType === 'email' ? ['password', 'passwordConfirm'] : []),
    ];

    const mobileSteps = [
      { title: '중개사무소/숙박업소 정보', fields: step1Fields, body: <RegStep1 loginType={loginType} /> },
      {
        title: '중개인 정보',
        fields: ['lastName', 'firstName', 'lastNameEN', 'firstNameEN', 'phoneNumber'],
        body: <RegStep2 />,
      },
      { title: '담당자 정보', fields: ['adminManagerInfos'], body: <RegStep3 /> },
    ];

    return (
      <FormProvider {...methods}>
        <RegisterWizard steps={mobileSteps} onSubmit={methods.handleSubmit(onSubmit, handleFormError)} />
      </FormProvider>
    );
  }

  return (
    <FormProvider {...methods}>
      <Container>
        <div style={{ width: '100%', maxWidth: '811px' }}>
          <T3>회원가입</T3>
          <Spacing height={6} />
          <Caption4 color="Gray30">(*표시는 필수입력입니다.)</Caption4>
          <Spacing height={48} />
          <Modal onSubmit={methods.handleSubmit(onSubmit, handleFormError)}>
            <ModalContainer>
              <Wrap gap={isMobile ? '12px' : '25px'} align="start" direction={isMobile ? 'column' : 'row'} width="100%">
                <Wrap width="150px" direction="column" align="start">
                  <Caption2>이메일</Caption2>
                  <Caption2 color="Gray30">*필수</Caption2>
                </Wrap>
                <Wrap direction="column" align="start" width="100%">
                  <Caption3>이메일</Caption3>
                  <Spacing height={8} />
                  <Input
                    width="502px"
                    type="email"
                    placeholder="이메일 주소를 입력하세요"
                    {...methods.register('email')}
                    error={Boolean(methods.formState.errors.email)}
                    errorMasaage={methods.formState.errors.email?.message}
                    disabled={true}
                  />
                </Wrap>
              </Wrap>

              {/* 비밀번호/비밀번호 확인: loginType이 email일 때만 노출 */}
              {loginType === 'email' && (
                <Wrap
                  gap={isMobile ? '12px' : '25px'}
                  align="start"
                  direction={isMobile ? 'column' : 'row'}
                  width="100%"
                >
                  <Wrap width="150px" direction="column" align="start">
                    <Caption2>비밀번호</Caption2>
                    <Caption2 color="Gray30">*필수</Caption2>
                  </Wrap>
                  <Wrap direction="column" align="start" width="100%">
                    <Caption3>비밀번호</Caption3>
                    <Spacing height={8} />
                    <Input
                      width="502px"
                      type="password"
                      placeholder="비밀번호를 입력하세요"
                      {...methods.register('password')}
                      error={Boolean(methods.formState.errors.password)}
                      errorMasaage={methods.formState.errors.password?.message}
                    />

                    <Caption3>비밀번호 확인</Caption3>
                    <Spacing height={8} />
                    <Input
                      width="502px"
                      type="password"
                      placeholder="비밀번호를 한 번 더 입력하세요"
                      {...methods.register('passwordConfirm')}
                      error={Boolean(methods.formState.errors.passwordConfirm)}
                      errorMasaage={methods.formState.errors.passwordConfirm?.message}
                    />
                  </Wrap>
                </Wrap>
              )}

              <Spacing height={40.5} />
              <Wrap gap={isMobile ? '12px' : '25px'} align="start" direction={isMobile ? 'column' : 'row'} width="100%">
                <Wrap direction="column" width="150px" align="start">
                  <Caption2>중개사무소 정보</Caption2>
                  <Caption2>/숙박업소 정보</Caption2>
                  <Caption2 color="Gray30">*필수</Caption2>
                </Wrap>
                <Wrap direction="column" align="start" width="100%">
                  <Wrap>
                    <Wrap
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        methods.setValue('agencyType', 'ESTATE');
                      }}
                    >
                      <Radiobox value={'ESTATE'} {...methods.register('agencyType')} />
                      <Spacing width={10} />
                      <Caption3>중개사무소입니다</Caption3>
                    </Wrap>
                    <Spacing width={36} />
                    <Wrap
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        methods.setValue('agencyType', 'ACCOMMODATION');
                      }}
                    >
                      <Radiobox value={'ACCOMMODATION'} {...methods.register('agencyType')} />
                      <Spacing width={10} />
                      <Caption3>숙박업소입니다</Caption3>
                    </Wrap>
                  </Wrap>
                  <Spacing height={16.5} />
                  <Wrap direction="column" align="start" width="100%">
                    <Caption3>중개사무소 혹은 숙박업소 이름(국문)</Caption3>
                    <Spacing height={8} />
                    <Controller
                      name={'agencyName'}
                      render={({ field }) => (
                        <Input
                          width="502px"
                          type="text"
                          placeholder="국문으로 입력하세요"
                          ref={field.ref}
                          value={field.value}
                          onChange={(event) => {
                            field.onChange(event.target.value);
                          }}
                          error={Boolean(methods.formState.errors.agencyName)}
                          errorMasaage={methods.formState.errors.agencyName?.message}
                        />
                      )}
                    />
                  </Wrap>
                  <Spacing height={16.5} />
                  <Wrap direction="column" align="start" width="100%">
                    <Caption3>중개사무소 혹은 숙박업소 이름(영문)</Caption3>
                    <Spacing height={8} />
                    <Controller
                      name={'agencyNameEN'}
                      render={({ field }) => (
                        <Input
                          width="502px"
                          type="text"
                          ref={field.ref}
                          placeholder="영문으로 입력하세요"
                          value={field.value}
                          onChange={(event) => {
                            field.onChange(event.target.value);
                          }}
                          error={Boolean(methods.formState.errors.agencyNameEN)}
                          errorMasaage={methods.formState.errors.agencyNameEN?.message}
                        />
                      )}
                    />
                  </Wrap>
                  {methods.watch('agencyType') === 'ESTATE' && (
                    <>
                      <Spacing height={16.5} />
                      <div>
                        <Wrap align="center">
                          <Caption3>개업 공인중개사 사무소 등록번호</Caption3>
                          <Spacing width={8} />
                          <img src="/WarningGray.svg" alt="warning" />
                          <Spacing width={1} />
                          <Caption3 color="Gray30">중개사무소 회원의 경우, 필수로 입력해주셔야 합니다.</Caption3>
                        </Wrap>
                        <Spacing height={8} />
                        <Controller
                          name={'registrationNumber'}
                          render={({ field }) => (
                            <Input
                              type="text"
                              placeholder="입력하세요"
                              width="502px"
                              ref={field.ref}
                              value={field.value}
                              onChange={(event) => {
                                field.onChange(event.target.value.replace(/[^0-9-]/g, ''));
                              }}
                              error={Boolean(methods.formState.errors.registrationNumber)}
                              errorMasaage={methods.formState.errors.registrationNumber?.message}
                            />
                          )}
                        />
                      </div>
                    </>
                  )}
                  <Spacing height={16.5} />
                  <Wrap direction="column" align="start" width="100%">
                    <Caption3 id="address">중개사무소 혹은 숙박업소 주소(국문)</Caption3>
                    <Spacing height={8} />
                    <Wrap gap="9px" align="start" width={isMobile ? '100%' : undefined}>
                      <Input
                        defaultFillErrorBoundary={false}
                        width="419px"
                        type="text"
                        placeholder="국문으로 입력하세요"
                        {...methods.register('address')}
                        disabled={true}
                        error={Boolean(methods.formState.errors.address)}
                        errorMasaage={methods.formState.errors.address?.message}
                      />
                      <SearchButton type="button" onClick={handleClickAddressSearch}>
                        <Caption3>주소검색</Caption3>
                      </SearchButton>
                    </Wrap>
                    <Spacing height={11} />
                    <Controller
                      name={'addressDetail'}
                      render={({ field }) => (
                        <Input
                          placeholder="상세주소를 입력해주세요"
                          width="419px"
                          type="text"
                          ref={field.ref}
                          error={Boolean(methods.formState.errors.addressDetail)}
                          value={field.value}
                          onChange={(event) => {
                            field.onChange(event.target.value);
                          }}
                          errorMasaage={methods.formState.errors.addressDetail?.message}
                        />
                      )}
                    />
                  </Wrap>
                  <Spacing height={16.5} />
                  <Wrap direction="column" align="start" width="100%">
                    <Wrap>
                      <Caption3>
                        {methods.watch('agencyType') === 'ESTATE' ? '부동산' : '숙박업소'} 대표 연락처
                      </Caption3>
                      <Spacing width={8} />
                      <img src="/WarningGray.svg" alt="warning" />
                      <Spacing width={1} />
                      <Caption3 color="Gray30">매물 상세에 등록되어 고객이 볼 수 있는 연락처 입니다.</Caption3>
                    </Wrap>
                    <Spacing height={8} />
                    <Controller
                      name={'agencyPhoneNumber'}
                      render={({ field }) => (
                        <Input
                          width="502px"
                          type="text"
                          placeholder="-없이 입력"
                          value={field.value}
                          ref={field.ref}
                          onChange={(event) => {
                            field.onChange(event.target.value.replace(/[^0-9]/g, ''));
                          }}
                          error={Boolean(methods.formState.errors.agencyPhoneNumber)}
                          errorMasaage={methods.formState.errors.agencyPhoneNumber?.message}
                        />
                      )}
                    />
                  </Wrap>
                </Wrap>
              </Wrap>

              <Spacing height={40.5} />
              <Wrap gap={isMobile ? '12px' : '25px'} align="start" direction={isMobile ? 'column' : 'row'} width="100%">
                <Wrap width="150px" direction="column" align="start">
                  <Caption2>중개인정보</Caption2>
                  <Caption2 color="Gray30">*필수</Caption2>
                </Wrap>
                <Wrap direction="column" align="start" width="100%">
                  <Wrap width={isMobile ? '100%' : undefined} gap={isMobile ? '15px' : undefined}>
                    <Wrap direction="column" align="start" width="100%">
                      <Caption3>성(국문)</Caption3>
                      <Spacing height={8} />
                      <Controller
                        name={'lastName'}
                        render={({ field }) => (
                          <Input
                            width="244px"
                            type="text"
                            placeholder="홍"
                            ref={field.ref}
                            value={field.value}
                            onChange={(event) => {
                              field.onChange(event.target.value);
                            }}
                            error={Boolean(methods.formState.errors.lastName)}
                            errorMasaage={methods.formState.errors.lastName?.message}
                          />
                        )}
                      />
                    </Wrap>
                    <Spacing width={15} />
                    <Wrap direction="column" align="start" width="100%">
                      <Caption3>이름(국문)</Caption3>
                      <Spacing height={8} />
                      <Controller
                        name={'firstName'}
                        render={({ field }) => (
                          <Input
                            width="244px"
                            type="text"
                            ref={field.ref}
                            placeholder="길동"
                            value={field.value}
                            onChange={(event) => {
                              field.onChange(event.target.value);
                            }}
                            error={Boolean(methods.formState.errors.firstName)}
                            errorMasaage={methods.formState.errors.firstName?.message}
                          />
                        )}
                      />
                    </Wrap>
                  </Wrap>
                  <Spacing height={5.5} />
                  <Wrap width={isMobile ? '100%' : undefined} gap={isMobile ? '15px' : undefined}>
                    <Wrap direction="column" align="start" width="100%">
                      <Caption3>성(영문)</Caption3>
                      <Spacing height={8} />
                      <Controller
                        name={'lastNameEN'}
                        render={({ field }) => (
                          <Input
                            width="244px"
                            type="text"
                            ref={field.ref}
                            placeholder="Hong"
                            value={field.value}
                            onChange={(event) => {
                              field.onChange(event.target.value);
                            }}
                            error={Boolean(methods.formState.errors.lastNameEN)}
                            errorMasaage={methods.formState.errors.lastNameEN?.message}
                          />
                        )}
                      />
                    </Wrap>
                    <Spacing width={15} />
                    <Wrap direction="column" align="start" width="100%">
                      <Caption3>이름(영문)</Caption3>
                      <Spacing height={8} />
                      <Controller
                        name={'firstNameEN'}
                        render={({ field }) => (
                          <Input
                            width="244px"
                            type="text"
                            ref={field.ref}
                            placeholder="Gildong"
                            value={field.value}
                            onChange={(event) => {
                              field.onChange(event.target.value);
                            }}
                            error={Boolean(methods.formState.errors.firstNameEN)}
                            errorMasaage={methods.formState.errors.firstNameEN?.message}
                          />
                        )}
                      />
                    </Wrap>
                  </Wrap>
                  <Spacing height={5.5} />
                  <Wrap direction="column" align="start" width="100%">
                    <Caption3>연락처(알림을 받을 연락처로 입력해주세요)</Caption3>
                    <Spacing height={8} />
                    <Controller
                      name={'phoneNumber'}
                      render={({ field }) => (
                        <Input
                          width="244px"
                          type="text"
                          ref={field.ref}
                          placeholder="-없이 입력"
                          value={field.value}
                          error={Boolean(methods.formState.errors.phoneNumber)}
                          onChange={(event) => {
                            field.onChange(event.target.value.replace(/[^0-9]/g, ''));
                          }}
                          errorMasaage={methods.formState.errors.phoneNumber?.message}
                        />
                      )}
                    />
                  </Wrap>
                </Wrap>
              </Wrap>

              <Spacing height={40.5} />
              <AdminManager />

              <Spacing height={40.5} />
              <Wrap gap={isMobile ? '12px' : '25px'} align="start" direction={isMobile ? 'column' : 'row'} width="100%">
                <Wrap width="150px">
                  <Caption2>가능한 외국어(복수선택)</Caption2>
                </Wrap>
                <Wrap gap="20px" wrap>
                  {Object.entries(Languages).map(([key, value]) => (
                    <Wrap
                      key={key}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        handleCheckArrayField('languages', key);
                      }}
                    >
                      <Checkbox {...methods.register('languages')} value={key} />
                      <Spacing width={10} />
                      <Caption3>{value}</Caption3>
                    </Wrap>
                  ))}
                </Wrap>
              </Wrap>
              <Spacing height={40.5} />
              <Wrap gap={isMobile ? '12px' : '25px'} align="start" direction={isMobile ? 'column' : 'row'} width="100%">
                <Wrap width="150px">
                  <Caption2>주거래 매물(복수선택)</Caption2>
                </Wrap>
                <Wrap gap="20px" wrap>
                  {Object.entries(MainListings).map(([key, value]) => (
                    <Wrap
                      key={key}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleCheckArrayField('mainListings', key)}
                    >
                      <Checkbox {...methods.register('mainListings')} value={key} />
                      <Spacing width={10} />
                      <Caption3>{value}</Caption3>
                    </Wrap>
                  ))}
                </Wrap>
              </Wrap>
              <Spacing height={81} />
              <Wrap width="100%" jutify="center">
                <SubmitButton type="submit">
                  <Caption3>회원가입</Caption3>
                </SubmitButton>
              </Wrap>
            </ModalContainer>
          </Modal>
        </div>
      </Container>
    </FormProvider>
  );
}

export default RegisterInfoPage;
